import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Lock, Plus, Send } from '../lib/icons';
import { fmt, PageTitle, StatBlock } from '../components/shared/badges';
import { useSondage } from '../lib/sondage';
import {
  ajouterPartenaireBourse,
  diffuserDemandeBourse,
  ecrireJetonAdmin,
  fetchCotationsBourse,
  fetchEtatBourse,
  fetchMessagesBourse,
  fetchPartenairesBourse,
  lireJetonAdmin,
  modifierPartenaireBourse,
} from '../lib/bourse';

const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';
const field =
  'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm outline-none focus:border-slate-900 focus:bg-white';
const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';

const ETATS = {
  simule: 'bg-slate-100 text-slate-600',
  en_attente: 'bg-sky-50 text-sky-800',
  envoye: 'bg-emerald-50 text-emerald-800',
  incertain: 'bg-amber-50 text-amber-800',
  echec: 'bg-red-50 text-red-800',
};

const ETATS_ENTRANTS = {
  retenu: 'Contribution retenue',
  aberrant: 'Prix aberrant — aucune réponse',
  sans_prix: 'Aucun prix détecté',
  numero_inconnu: 'Numéro non autorisé',
  numero_invalide: 'Numéro invalide',
  recu: 'Reçu',
};

export default function Bourse() {
  const [jeton, setJeton] = useState(lireJetonAdmin());
  const [etat, setEtat] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [partenaires, setPartenaires] = useState([]);
  const [cotations, setCotations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [nouveau, setNouveau] = useState({ name: '', phone: '' });

  const load = useCallback(async () => {
    if (!lireJetonAdmin()) return;
    const [e, p, c, m] = await Promise.all([
      fetchEtatBourse(),
      fetchPartenairesBourse(),
      fetchCotationsBourse(),
      fetchMessagesBourse(),
    ]);
    const echec = [e, p, c, m].find((resultat) => resultat.error);
    if (echec) {
      setErreur(echec.error);
      setEtat(null);
      return;
    }
    setErreur(null);
    setEtat(e.data);
    setPartenaires(p.data ?? []);
    setCotations(c.data ?? []);
    setMessages(m.data ?? []);
    return [JSON.stringify(e.data ?? null), (p.data ?? []).length, (c.data ?? []).length, (m.data ?? []).length];
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  useSondage(load, 20000);

  const connecter = (e) => {
    e.preventDefault();
    ecrireJetonAdmin(jeton.trim());
    load();
  };

  const ajouter = async (e) => {
    e.preventDefault();
    const { error } = await ajouterPartenaireBourse({ ...nouveau, active: true, consent: true });
    if (error) return toast.error(error);
    toast.success(`Partenaire « ${nouveau.name} » enregistré`);
    setNouveau({ name: '', phone: '' });
    load();
  };

  const basculer = async (p, champ) => {
    const { error } = await modifierPartenaireBourse(p._id, { [champ]: !p[champ] });
    if (error) return toast.error(error);
    load();
  };

  const diffuser = async () => {
    const { error, data } = await diffuserDemandeBourse();
    if (error) return toast.error(error);
    toast.success(
      data.duplicate
        ? 'Demande déjà enregistrée — aucun doublon envoyé.'
        : `Demande mise en file pour ${data.queued} partenaire(s)${data.send_enabled ? '' : ' — envoi réel désactivé (simulation)'}`
    );
    load();
  };

  if (!lireJetonAdmin() || erreur)
    return (
      <div data-testid="bourse-connexion" className="space-y-5">
        <PageTitle titre="Bourse Numérique de l’Huile de Sfax" sous="Référence de marché indicative — accès administrateur" />
        <form onSubmit={connecter} className="max-w-xl space-y-3 border border-slate-200 bg-white px-5 py-5">
          <p className="text-sm text-slate-600">
            Ce module est protégé par un secret serveur dédié. Saisissez le jeton d’administration de la Bourse : il
            reste dans cette session de navigateur et n’est jamais stocké dans l’application.
          </p>
          {erreur && (
            <p data-testid="bourse-erreur" className="border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800">{erreur}</p>
          )}
          <div>
            <label className={label}>Jeton d’administration</label>
            <input data-testid="bourse-jeton" type="password" value={jeton} onChange={(e) => setJeton(e.target.value)} className={field} />
          </div>
          <button type="submit" data-testid="bourse-connecter" className="h-11 bg-slate-900 px-5 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white">
            Ouvrir le module
          </button>
        </form>
      </div>
    );

  if (!etat) return <p className="px-4 py-6 text-sm text-slate-500">Chargement de la bourse…</p>;
  const ref = etat.reference;

  return (
    <div data-testid="bourse-module" className="space-y-5">
      <PageTitle titre="Bourse Numérique de l’Huile de Sfax" sous="Référence de marché indicative par SMS entre partenaires" />

      <p data-testid="bourse-avertissement" className="flex items-start gap-2 border-l-4 border-slate-900 bg-slate-50 px-4 py-3 text-xs text-slate-700">
        <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
        <span>{ref.notice} Fenêtre glissante de {ref.window_hours} h · référence « provisoire » en dessous de {ref.min_contributors} contributeurs. Écart maximal toléré par SMS : ±20 % de la médiane.</span>
      </p>

      {!etat.cloud_reception_configured && (
        <p role="alert" className="border border-amber-300 bg-amber-50 p-3 text-sm">
          Réception SMS Gate Cloud non configurée : renseigner la clé de signature Webhooks du téléphone et son Device ID côté serveur, puis enregistrer le webhook.
        </p>
      )}
      <div data-testid="bourse-entrants" className="flex flex-wrap items-center gap-2 text-[11px]">
        {Object.entries(etat.inbound ?? {}).map(([cle, n]) => (
          <span
            key={cle}
            className={`border px-2 py-1 font-data ${cle === 'aberrant' ? 'border-red-300 bg-red-50 text-red-800' : 'border-slate-200 bg-slate-50 text-slate-600'}`}
          >
            {(ETATS_ENTRANTS[cle] ?? cle)} : {n}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        <StatBlock testid="bourse-extra" titre="Médiane Extra (TND/kg)" accent="border-l-emerald-600" items={[['Référence', ref.label_extra]]} />
        <StatBlock testid="bourse-lampante" titre="Médiane Lampante (TND/kg)" accent="border-l-orange-500" items={[['Référence', ref.label_lampante]]} />
        <StatBlock
          testid="bourse-contributeurs"
          titre="Contributeurs retenus"
          accent="border-l-sky-600"
          items={[['Sur la fenêtre', fmt(ref.contributors)], ['Statut', ref.provisional ? 'Provisoire' : 'Consolidée']]}
        />
        <StatBlock
          testid="bourse-passerelle"
          titre="Passerelle Android"
          accent="border-l-slate-900"
          items={[
            ['Envoi réel', etat.send_enabled ? 'Activé' : 'Désactivé'],
            ['Configurée', etat.gateway_configured ? 'Oui' : 'Non'],
          ]}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" data-testid="bourse-diffuser" onClick={diffuser} className="flex h-11 items-center gap-2 border-2 border-slate-900 px-4 font-heading text-sm font-bold uppercase tracking-[0.06em] text-slate-900 hover:bg-slate-900 hover:text-white">
          <Send size={15} /> Demander les prix aux partenaires
        </button>
        <span className="font-data text-[11px] text-slate-500">
          Délai minimal entre deux demandes : {etat.broadcast_cooldown_seconds / 60} min ·{' '}
          {etat.send_enabled ? 'les SMS partent réellement' : 'aucun SMS réel n’est envoyé (simulation)'}
        </span>
      </div>

      <section className="border border-slate-200 bg-white">
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-2">
          <p className="font-heading text-sm font-bold text-slate-900">Partenaires autorisés</p>
        </div>
        <form onSubmit={ajouter} className="grid grid-cols-1 gap-3 border-b border-slate-200 px-4 py-3 sm:grid-cols-3">
          <div>
            <label className={label}>Nom</label>
            <input data-testid="bourse-partenaire-nom" value={nouveau.name} onChange={(e) => setNouveau({ ...nouveau, name: e.target.value })} className={field} />
          </div>
          <div>
            <label className={label}>Numéro tunisien</label>
            <input data-testid="bourse-partenaire-tel" value={nouveau.phone} onChange={(e) => setNouveau({ ...nouveau, phone: e.target.value })} placeholder="+216 …" className={field} />
          </div>
          <button type="submit" data-testid="bourse-partenaire-ajouter" className="mt-auto flex h-11 items-center justify-center gap-2 bg-slate-900 px-4 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white">
            <Plus size={15} /> Ajouter
          </button>
        </form>
        <table className="w-full border-collapse text-left" data-testid="bourse-partenaires">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {['Nom', 'Numéro', 'Actif', 'Consentement', ''].map((c, i) => (
                <th key={c || i} className={th}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {partenaires.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-sm text-slate-500">Aucun partenaire enregistré.</td></tr>
            ) : (
              partenaires.map((p) => (
                <tr key={p._id} data-testid={`bourse-partenaire-${p._id}`} className="border-b border-slate-100">
                  <td className="px-3 py-2 text-sm font-semibold text-slate-900">
                    {p.name}
                    <span className="ml-2 font-data text-[10px] font-normal text-slate-500">
                      {(p.anomaly_streak ?? 0) + '/3 anomalies'}
                    </span>
                    {p.fraud_banned && (
                      <span className="ml-2 font-data text-[10px] font-semibold text-red-700">
                        Suspendu : prix aberrants
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-data text-sm text-slate-700">{p.phone}</td>
                  <td className="px-3 py-2 text-xs">{p.active ? 'Oui' : 'Non'}</td>
                  <td className="px-3 py-2 text-xs">{p.consent ? 'Oui' : 'Non'}</td>
                  <td className="px-3 py-2 text-right">
                    <button type="button" onClick={() => basculer(p, 'active')} className="mr-1 border border-slate-300 px-2 py-1 text-[11px] font-semibold hover:bg-slate-900 hover:text-white">
                      {p.active ? 'Désactiver' : 'Activer'}
                    </button>
                    <button type="button" onClick={() => basculer(p, 'consent')} className="border border-slate-300 px-2 py-1 text-[11px] font-semibold hover:bg-slate-900 hover:text-white">
                      {p.consent ? 'Retirer le consentement' : 'Consentement'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <section className="border border-slate-200 bg-white">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-2">
            <p className="font-heading text-sm font-bold text-slate-900">Contributions retenues ({ref.window_hours} h)</p>
          </div>
          <table className="w-full border-collapse text-left" data-testid="bourse-cotations">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Partenaire', 'Qualité', 'Prix', 'Reçu le'].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cotations.length === 0 ? (
                <tr><td colSpan={4} className="px-3 py-8 text-center text-sm text-slate-500">Aucune contribution sur la fenêtre.</td></tr>
              ) : (
                cotations.map((q) => (
                  <tr key={`${q.message_id}-${q.quality}`} className="border-b border-slate-100">
                    <td className="px-3 py-2 text-sm text-slate-900">{q.partner_name}</td>
                    <td className="px-3 py-2 text-xs uppercase text-slate-600">{q.quality}</td>
                    <td className="tabular px-3 py-2 font-data text-sm font-semibold">{q.price}</td>
                    <td className="px-3 py-2 font-data text-[11px] text-slate-500">
                      {new Date(q.observed_at).toISOString().slice(0, 16).replace('T', ' ')}
                      {q.warning && <span className="ml-1 text-amber-700">· {q.warning}</span>}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>

        <section className="border border-slate-200 bg-white">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-2">
            <p className="font-heading text-sm font-bold text-slate-900">File de messages</p>
          </div>
          <table className="w-full border-collapse text-left" data-testid="bourse-messages">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Destinataire', 'Type', 'État', 'Tentatives'].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {messages.length === 0 ? (
                <tr><td colSpan={4} className="px-3 py-8 text-center text-sm text-slate-500">Aucun message en file.</td></tr>
              ) : (
                messages.map((m) => (
                  <tr key={m._id} className="border-b border-slate-100">
                    <td className="px-3 py-2 font-data text-sm text-slate-700">{m.phone}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{m.kind}</td>
                    <td className="px-3 py-2">
                      <span className={`font-data text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 ${ETATS[m.state] ?? 'bg-slate-100 text-slate-600'}`}>
                        {m.state === 'simule' ? <><Lock size={10} className="mr-1 inline" />simulé</> : m.state}
                      </span>
                    </td>
                    <td className="tabular px-3 py-2 font-data text-sm text-slate-700">{m.attempts ?? 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}
